import React from 'react';
import { rem, Checkbox } from '@mantine/core';
import { Table as TanstackTable } from '@tanstack/react-table';
import { flexRender } from '@dealstack/frontend/shared/tables/flexRender';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { Z_INDEX } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import {
	getFinalSelectionCount,
	SelectionScope,
	setAllRowsSelected,
	setAllVisibleRowsSelected,
} from '@dealstack/frontend/shared/components/Table/MantineTable/table-utils';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { ColumnHeader } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnHeader/ColumnHeader';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { ColumnResizeHandle } from './ColumnResizeHandle';
import { TableData, RowActionRenderer, getTableMeta } from './types';
import { UseTableGridLayoutReturn } from './hooks/useTableGridLayout';
import {
	getPinnedColumnInfo,
	getPinnedColumnStyle,
	LEADING_TRACK_WIDTH,
} from './utils/pinnedColumn';
import { isFixedWidthColumn } from './utils/generateGridTemplate';
import { TEST_ELEMENT_IDS } from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';

interface Props<T extends TableData> {
	table: TanstackTable<T>;
	gridLayout: UseTableGridLayoutReturn;
	enableRowDragAndDrop?: boolean;
	enableRowSelection?: boolean;
	showRowSelection?: boolean;
	pinSelectionColumn?: boolean;
	enableRowActions?: boolean;
	renderRowActions?: RowActionRenderer<T>;
	renderRowActionMenuItems?: RowActionRenderer<T>;
	enableStickyHeader?: boolean;
	enableColumnResizing?: boolean;
	selectionScope: SelectionScope;
	setSelectionScope: (scope: SelectionScope) => void;
	enableVerticalCellBorders?: boolean;
}

export const MantineTableHeader = <T extends TableData>({
	table,
	gridLayout,
	enableRowDragAndDrop,
	enableRowSelection,
	showRowSelection,
	pinSelectionColumn = false,
	enableRowActions,
	renderRowActions,
	renderRowActionMenuItems,
	enableStickyHeader,
	enableColumnResizing,
	setSelectionScope,
	enableVerticalCellBorders = false,
}: Props<T>) => {
	const isManualPagination = getTableMeta(table).manualPagination;
	const isGreedySelecting = getTableMeta(table).greedySelecting;
	const { pinnedColumns, lastPinnedColumnId } = getTableMeta(table);
	const isPinnedMode = !!pinnedColumns && Object.keys(pinnedColumns).length > 0;
	// In pinned mode the vertical stickiness lives on the full-width wrapper
	// below (so cells get a full-width containing block for sticky-left);
	// otherwise each cell sticks to the top itself.
	const stickyHeaderOnCell = enableStickyHeader && !isPinnedMode;

	const getIsIndeterminate = () => {
		const selectionState = table.getState().rowSelection;
		const selectedCount = Object.values(selectionState).filter(Boolean).length;
		const hasSomeRowsSelected = selectedCount > 0;
		const totalCount = table.getRowCount();

		if (isManualPagination && isGreedySelecting) {
			return selectedCount > 0 && selectedCount < totalCount;
		} else if (isManualPagination) {
			const rows = table.getRowModel().rows;
			const selectedRowCount = rows.filter((row) => row.getIsSelected()).length;
			const unselectedRowCount = rows.length - selectedRowCount;

			return (
				(selectedRowCount > 0 && unselectedRowCount > 0) ||
				(selectedCount > 0 && unselectedRowCount > 0)
			);
		}

		return (
			hasSomeRowsSelected &&
			!table.getIsAllPageRowsSelected() &&
			!table.getIsAllRowsSelected()
		);
	};

	const getIsChecked = () => {
		const selectedCount = getFinalSelectionCount(table);
		const hasSomeRowsSelected = selectedCount > 0;

		if (isManualPagination && isGreedySelecting) {
			return hasSomeRowsSelected;
		} else if (isManualPagination) {
			return (
				table.getIsAllPageRowsSelected() ||
				table.getIsAllRowsSelected() ||
				hasSomeRowsSelected
			);
		}

		const isChecked =
			hasSomeRowsSelected || table.getIsAllPageRowsSelected() || table.getIsAllRowsSelected();
		return isChecked;
	};

	const headerRows = table.getHeaderGroups().map((headerGroup, groupIndex) => {
		let gridColumnIndex = 1;

		return (
			<React.Fragment key={headerGroup.id}>
				{/* Drag handle column */}
				{enableRowDragAndDrop && (
					<div
						key={`${headerGroup.id}-drag-handle`}
						style={{
							gridColumn: gridColumnIndex++,
							gridRow: groupIndex + 1,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							padding: '8px',
							minHeight: rem('48px'),
							...(stickyHeaderOnCell && {
								position: 'sticky',
								top: 0,
								zIndex: Z_INDEX.STICKY_HEADER,
								backgroundColor: 'white',
							}),
							borderBottom: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
						}}
					>
						<Icon
							variant="drag-indicator"
							size="sm"
							color={defaultTheme.v3.colors.gray[6]}
						/>
					</div>
				)}

				{/* Selection column */}
				{enableRowSelection && showRowSelection && (
					<div
						key={`${headerGroup.id}-select-all`}
						style={{
							gridColumn: gridColumnIndex++,
							gridRow: groupIndex + 1,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							padding: '8px',
							minHeight: rem('48px'),
							...(stickyHeaderOnCell && {
								position: 'sticky',
								top: 0,
								zIndex: Z_INDEX.STICKY_HEADER,
								backgroundColor: 'white',
							}),
							...(pinSelectionColumn &&
								getPinnedColumnStyle(true, Z_INDEX.STICKY_PINNED_HEADER, {
									backgroundColor: 'white',
									left: enableRowDragAndDrop ? LEADING_TRACK_WIDTH : 0,
									withShadow: false,
								})),
							borderBottom: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
						}}
					>
						<Checkbox
							data-testid={TEST_ELEMENT_IDS.CAP_TABLE_SELECT_ALL_ON_PAGE_CHECKBOX}
							checked={getIsChecked()}
							indeterminate={getIsIndeterminate()}
							onChange={() => {
								const isChecked = getIsChecked();
								const isIndeterminate = getIsIndeterminate();
								const meta = getTableMeta(table);

								if (meta.manualPagination && meta.greedySelecting) {
									if (isChecked && isIndeterminate) {
										setAllRowsSelected(table, false);
										meta.setGreedySelecting(false);
										setSelectionScope(SelectionScope.Page);
									} else if (isChecked) {
										setAllRowsSelected(table, false);
										meta.setGreedySelecting(false);
										setSelectionScope(SelectionScope.Page);
									} else {
										setAllRowsSelected(table, true);
									}
									return;
								}

								if (meta.manualPagination) {
									if (isChecked && isIndeterminate) {
										table.setRowSelection({});
									} else if (isChecked) {
										setAllRowsSelected(table, false);
									} else {
										setAllRowsSelected(table, true);
									}
									return;
								}

								if (meta.selectionScope === SelectionScope.All) {
									if (isChecked) {
										setSelectionScope(SelectionScope.Page);
										setAllRowsSelected(table, false);
									} else {
										setAllRowsSelected(table, false);
									}
								}

								if (meta.selectionScope === SelectionScope.Page) {
									if (isChecked && isIndeterminate) {
										table.setRowSelection({});
									} else if (isChecked) {
										setAllVisibleRowsSelected(table, false);
									} else {
										setAllVisibleRowsSelected(table, true);
									}
								}
							}}
							onClick={(e) => e.stopPropagation()}
							size="sm"
						/>
					</div>
				)}

				{/* Data column headers */}
				{headerGroup.headers.map((header) => {
					const span = gridLayout.getGridColumnSpanForHeader(header);
					const hasSubHeaders = header.subHeaders && header.subHeaders.length > 0;

					// Check if there are any parent columns in this header group
					const hasParentColumns = table
						.getHeaderGroups()
						.some((hg) =>
							hg.headers.some((h) => h.subHeaders && h.subHeaders.length > 0)
						);

					// Determine if this is the last child of a parent column
					let isLastChildHeader = false;
					if (hasParentColumns && !hasSubHeaders) {
						for (const parentHeader of table.getHeaderGroups()[0].headers) {
							if (
								parentHeader.subHeaders &&
								parentHeader.subHeaders.length > 0 &&
								parentHeader.subHeaders.some((sh) => sh.id === header.id)
							) {
								const lastChildId =
									parentHeader.subHeaders[parentHeader.subHeaders.length - 1].id;
								isLastChildHeader = header.id === lastChildId;
								break;
							}
						}
					}

					const isLastColumn =
						headerGroup.headers[headerGroup.headers.length - 1].id === header.id;

					const currentGridColumn = gridColumnIndex;
					gridColumnIndex += span;

					const totalHeaderRows = table.getHeaderGroups().length;
					const shouldSpanAllRows =
						groupIndex === 0 &&
						!hasSubHeaders &&
						hasParentColumns &&
						totalHeaderRows > 1;

					const gridRowValue = shouldSpanAllRows
						? `1 / span ${totalHeaderRows}`
						: groupIndex + 1;

					return (
						<div
							key={header.id}
							style={{
								gridColumn:
									span > 1
										? `${currentGridColumn} / span ${span}`
										: currentGridColumn,
								gridRow: gridRowValue,
								display: 'flex',
								alignItems: 'center',
								padding: '8px',
								minHeight: rem('48px'),
								position: 'relative',
								overflow: 'visible',
								...(stickyHeaderOnCell && {
									position: 'sticky',
									top: 0,
									zIndex: Z_INDEX.STICKY_HEADER,
									backgroundColor: 'white',
								}),
								borderBottom: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
								...(enableVerticalCellBorders &&
									!isLastColumn && {
										borderRight: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
									}),
								...(hasParentColumns &&
									(hasSubHeaders || isLastChildHeader) &&
									!isLastColumn && {
										borderRight: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
									}),
								...(() => {
									const pin = getPinnedColumnInfo(
										pinnedColumns,
										lastPinnedColumnId,
										header.column.id
									);
									return getPinnedColumnStyle(
										pin.isPinned,
										Z_INDEX.STICKY_PINNED_HEADER,
										{
											backgroundColor: 'white',
											left: pin.left,
											withShadow: pin.isLast,
										}
									);
								})(),
							}}
						>
							{header.isPlaceholder
								? null
								: (() => {
										const columnDef = header.column
											.columnDef as ExtendedColumnDef<T, unknown>;
										const enableSorting = columnDef.enableSorting ?? true;

										if (typeof columnDef.header === 'function') {
											return flexRender(
												columnDef.header,
												header.getContext()
											);
										}

										return (
											<ColumnHeader
												context={header.getContext()}
												enableSorting={enableSorting}
											>
												{flexRender(columnDef.header, header.getContext())}
											</ColumnHeader>
										);
									})()}
							{enableColumnResizing &&
								header.column.getCanResize() &&
								!isFixedWidthColumn(
									header.column.columnDef as ExtendedColumnDef<T, unknown>
								) && <ColumnResizeHandle header={header} />}
						</div>
					);
				})}

				{/* Actions column */}
				{enableRowActions && (renderRowActions || renderRowActionMenuItems) && (
					<div
						key={`${headerGroup.id}-actions`}
						style={{
							gridColumn: gridColumnIndex,
							gridRow: groupIndex + 1,
							display: 'flex',
							alignItems: 'center',
							padding: '8px',
							minHeight: rem('48px'),
							...(stickyHeaderOnCell && {
								position: 'sticky',
								top: 0,
								zIndex: Z_INDEX.STICKY_HEADER,
								backgroundColor: 'white',
							}),
							borderBottom: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
						}}
					>
						Actions
					</div>
				)}
			</React.Fragment>
		);
	});

	if (isPinnedMode) {
		return (
			<div
				style={{
					gridColumn: '1 / -1',
					display: 'grid',
					gridTemplateColumns: gridLayout.gridTemplateColumns,
					backgroundColor: 'white',
					...(enableStickyHeader && {
						position: 'sticky',
						top: 0,
						zIndex: Z_INDEX.STICKY_HEADER,
					}),
				}}
			>
				{headerRows}
			</div>
		);
	}

	return <>{headerRows}</>;
};
